import { z } from 'zod';
import { SALES_CHANNELS, US_STATE_CODES } from '../enums';
import type { RecordStatus } from '../enums';
import { listQuerySchema, optionalText, recordStatus } from './common';
import { registerReportDefs, reportColumn } from './reports';
import type { ReportDef } from './reports';
import type { UserRef } from './users';

/**
 * Settings lists: the simple "form above a list" screens of the reference's Settings area
 * that share one shape. A list is a definition (heading, permission code, fields); one
 * server module stores and validates the records of every list and one page renders them.
 *
 * Field labels, option lists and column headers are the reference's (product inventory,
 * line "Shipper Settings (236)" and following, and appendix 11.5 and 11.6). Where the
 * reference's add form was not seen, the definition says so in `note`, and that note is
 * shown on the page together with what the list does not do yet.
 */

export type SettingsRefSource = 'plan' | 'noteType' | 'carrierCode' | 'topup';

export type SettingsField = {
  key: string;
  label: string;
  required?: boolean;
  help?: string;
  /** Shown only while another field has this value (the reference's per-shipper blocks); cleared otherwise. */
  when?: { key: string; equals: string };
  /** False: kept off the list's columns (a detail of the form). */
  listed?: false;
  /** A field added after a deployment: offered only while this release feature is on. */
  feature?: string;
} & (
  | { type: 'text'; maxLength?: number; multiline?: boolean }
  | { type: 'email' }
  | { type: 'digits'; length: number }
  | { type: 'zip' }
  | { type: 'state' }
  | { type: 'select'; options: readonly string[] }
  | { type: 'multiselect'; options: readonly string[] }
  /** Dollars on screen, integer cents in the record. */
  | { type: 'money' }
  /** A percentage with two decimals on screen, integer hundredths of a percent in the record. */
  | { type: 'percent' }
  | { type: 'number'; min: number; max: number }
  /** A record of another screen, stored by id. */
  | { type: 'ref'; source: SettingsRefSource; multiple?: boolean }
);

export interface SettingsListDef {
  key: string;
  /** Page heading. */
  title: string;
  code: string;
  /** Label of the button that opens the form. */
  addLabel: string;
  /** What one record is called in messages. */
  itemLabel: string;
  fields: readonly SettingsField[];
  /** Fields that identify a record: two records may not share all of them. */
  unique: readonly string[];
  /** The record has a Status (Active / Inactive). */
  status: boolean;
  searchLabel: string;
  /** Text fields the search box looks at. */
  searchKeys: readonly string[];
  /** Extra list columns worked out by the server (`labels[key]`), after the field columns. */
  derived?: ReadonlyArray<{ key: string; header: string; after: string }>;
  /** What was not seen on the reference and what the list does not do yet. */
  note: string;
}

/** "Transaction Type" of Tax Config, as the reference lists it. */
export const TAX_TRANSACTION_TYPES = [
  'ActivationFee',
  'DirectoryAssistance',
  'EquipmentPurchase',
  'LateFee',
  'MdnChange',
  'PaperBillingFee',
  'ProcessingFee',
  'ReconnectionFees',
  'RegularMonthlyBilling',
  'RestockingFee',
  'RestoreFee',
  'ReturnCheckFee',
  'ShippingCharge',
  'SimPurchase',
  'Topup',
] as const;
export const TAX_CUSTOMER_TYPES = [
  'RESIDENTIAL(01)',
  'COMMERCIAL(02)',
  'INDUSTRIAL(03)',
  'SENIOR CITIZEN(04)',
  'TRIBAL GOVERNMENT(05)',
  'FEDERAL GOVERNMENT(06)',
  'STATE GOVERNMENT(07)',
  'LOCAL GOVERNMENT(08)',
  'RESERVED(09)',
  'LIFELINE(10)',
  'MANUFACTURING(11)',
  'RETAIL(12)',
  'TRIBAL MEMBER(13)',
  'AGRICULTURE(14)',
] as const;
export const TAX_BUSINESS_TYPES = [
  'RETAIL(01)',
  'MANUFACTURING RETAILER (RESERVED)(02)',
  'MANUFACTURING WHOLESALER (RESERVED)(03)',
  'COMPETITIVE LOCAL EXCHANGE CARRIER (CLEC)(10)',
  'INTEREXCHANGE CARRIER (IXC)(11)',
  'COMMERCIAL MOBILE RADIO SERVICE (CMRS / WIRELESS)(12)',
  'INCUMBENT LOCAL EXCHANGE CARRIER (ILEC)(13)',
  'PREPAID WIRELESS(14)',
  'VOICE OVER INTERNET PROTOCOL (VOIP)(15)',
  'TV PROVIDER(16)',
  'NON-REGULATED RESELLER(17)',
  'ENERGY TRADING(18)',
  'TRADITIONAL REGULATED UTILITY(19)',
  'REGULATED DISTRIBUTOR IN DEREGULATED MARKET(20)',
] as const;
export const TAX_LEVELS = [
  'FEDERAL(1)',
  'STATE/PROVINCE(2)',
  'COUNTY(3)',
  'CITY/TOWN/VILLAGE(4)',
  'DISTRICT(5)',
  'REPORTING AGENCY(6)',
  'NEXT(7)',
] as const;
export const TAX_SITUS_RULES = [
  'Toll Calling(C)',
  'Order Approval(I)',
  'Private Line(L)',
  'Order Placement(P)',
  'Customer Address(S)',
  'Ship To(T)',
  'Ship From(V)',
] as const;

const YES_NO = ['Yes', 'No'] as const;
/** Shippers of the reference's Shipper Settings select, in its order. */
const SETTING_SHIPPERS = ['USPS', 'UPS', 'STAMPS', 'SHIPST', 'NONE', 'FEDEX', 'ENDICIA'] as const;

/**
 * The package settings of the reference's Endicia form (walk of 2026-10-21): Weight Oz, Mail Class
 * {FIRST, PRIORITY}, Mailpiece Shape {Flat, Parcel}, the dimensions, Cost Center and Tax
 * Identification No. They are sent with each Endicia label request. Assumptions: the dimensions are
 * one field (their own labels were not recorded), and the lengths and the weight's range are ours.
 */
const ENDICIA = { key: 'shipper', equals: 'ENDICIA' } as const;
const PACKAGE_FEATURE = 'shipper-package-settings';
const ENDICIA_PACKAGE_FIELDS: SettingsField[] = [
  { key: 'weightOz', label: 'Weight Oz', type: 'number', min: 0, max: 1120 },
  { key: 'mailClass', label: 'Mail Class', type: 'select', options: ['FIRST', 'PRIORITY'] },
  { key: 'mailpieceShape', label: 'Mailpiece Shape', type: 'select', options: ['Flat', 'Parcel'] },
  {
    key: 'dimensions',
    label: 'Dimensions',
    type: 'text',
    maxLength: 30,
    help: 'Length x width x height, in inches',
  },
  { key: 'costCenter', label: 'Cost Center', type: 'text', maxLength: 30 },
  { key: 'taxId', label: 'Tax Identification No', type: 'text', maxLength: 30 },
].map((f) => ({ ...f, when: ENDICIA, listed: false, feature: PACKAGE_FEATURE }) as SettingsField);

const blacklist = (
  key: string,
  title: string,
  fields: readonly SettingsField[],
  unique: readonly string[],
  what: string,
): SettingsListDef => ({
  key,
  title,
  code: 'PC984',
  addLabel: 'Add',
  itemLabel: 'Entry',
  fields,
  unique,
  status: true,
  searchLabel: title,
  searchKeys: unique,
  note: `An enrollment whose ${what} is on this list with status Active is held for the fraud review queue, or refused when the review queue is switched off (assumption: the reference shows the list and a search box only; how entries are added and what they do was not seen).`,
});

export const SETTINGS_LISTS: readonly SettingsListDef[] = [
  {
    key: 'customer-classifications',
    title: 'Customer Classification',
    code: 'PC787',
    addLabel: 'Add',
    itemLabel: 'Customer classification',
    fields: [
      {
        key: 'name',
        label: 'Customer Classification',
        type: 'text',
        required: true,
        maxLength: 60,
      },
    ],
    unique: ['name'],
    status: true,
    searchLabel: 'Customer Classification',
    searchKeys: ['name'],
    note: 'The choices of "Update Customer Classification" on a customer page. Assumption: the reference has the dialog and a list per tenant, but no screen to manage the list was found; this one is reached from the dialog.',
  },
  {
    key: 'discount-credit-types',
    title: 'Manage Discount Credit Types',
    code: 'PC843',
    addLabel: 'Add',
    itemLabel: 'Discount credit type',
    fields: [
      {
        key: 'name',
        label: 'Discount Credit Type Name',
        type: 'text',
        required: true,
        maxLength: 100,
      },
    ],
    unique: ['name'],
    status: false,
    searchLabel: 'Discount Credit Type Name',
    searchKeys: ['name'],
    note: 'The types offered by "Add Discount Credit" on a customer\'s Billing page.',
  },
  blacklist(
    'email-blacklist',
    'Email Blacklist',
    [{ key: 'email', label: 'Email', type: 'email', required: true }],
    ['email'],
    'email address',
  ),
  blacklist(
    'phone-blacklist',
    'Phone Number Blacklist',
    [{ key: 'phone', label: 'Phone Number', type: 'digits', length: 10, required: true }],
    ['phone'],
    'phone number',
  ),
  blacklist(
    'address-blacklist',
    'Address Blacklist',
    [
      { key: 'address1', label: 'Address1', type: 'text', required: true, maxLength: 100 },
      { key: 'address2', label: 'Address2', type: 'text', maxLength: 100 },
      { key: 'state', label: 'State', type: 'state', required: true },
      { key: 'city', label: 'City', type: 'text', required: true, maxLength: 60 },
      { key: 'zip', label: 'Zipcode', type: 'zip', required: true },
    ],
    ['address1', 'address2', 'zip'],
    'service address (address 1, address 2 and ZIP code)',
  ),
  {
    key: 'sub-note-types',
    title: 'Manage Sub Note Type',
    code: 'PC1044',
    addLabel: 'Add Sub Note Type',
    itemLabel: 'Sub note type',
    fields: [
      { key: 'parent', label: 'Parent Nodes', type: 'ref', source: 'noteType', required: true },
      { key: 'name', label: 'Note Type', type: 'text', required: true, maxLength: 100 },
      {
        key: 'notes',
        label: 'Notes',
        type: 'text',
        required: true,
        maxLength: 1000,
        multiline: true,
      },
    ],
    unique: ['parent', 'name'],
    status: true,
    searchLabel: 'Search Notes',
    searchKeys: ['name', 'notes'],
    note: 'Sub note types are kept but not yet offered when a note is added to a customer: the note form read from the reference has no field for them.',
  },
  {
    key: 'promo-discounts',
    title: 'Add Promo Discounts Plan',
    code: 'PC1076',
    addLabel: 'Add Promo Discount',
    itemLabel: 'Promo discount',
    fields: [
      { key: 'name', label: 'Discount Name', type: 'text', required: true, maxLength: 100 },
      { key: 'amountCents', label: 'Amount', type: 'money', required: true },
      { key: 'plan', label: 'Plan Code', type: 'ref', source: 'plan', required: true },
      {
        key: 'months',
        label: 'Allowed Renewal (in month(s))',
        type: 'select',
        options: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'],
        required: true,
      },
    ],
    unique: ['name'],
    status: false,
    searchLabel: 'Discount Name',
    searchKeys: ['name'],
    note: 'A promo discount comes off the plan price when a customer on that plan renews (Plan Renew), for as many renewals as "Allowed Renewal" says. That reading of the form is this project\'s; the reference list of promo discounts was not seen.',
  },
  {
    key: 'payment-gateways',
    title: 'Payment Gateway',
    code: 'PC239',
    addLabel: 'Add',
    itemLabel: 'Payment gateway',
    fields: [
      { key: 'gatewayName', label: 'Gateway Name', type: 'text', required: true, maxLength: 80 },
      { key: 'nickName', label: 'Nick Name', type: 'text', required: true, maxLength: 80 },
    ],
    unique: ['gatewayName', 'nickName'],
    status: true,
    searchLabel: 'Gateway Name',
    searchKeys: ['gatewayName', 'nickName'],
    note: 'No payment gateway is connected. This list records which gateways the business uses; the reference\'s "Add" form was not seen, and no account names, keys or passwords are asked for or stored. No payment goes through a gateway.',
  },
  {
    // The reference's "Tax Config Setting" form (walk of 2026-10-14). The tax engine's own
    // catalogues (about 1,010 PS codes, 101 tax types, 237 tax type classes) are not copied: the
    // codes are typed. A transaction type has one line, as the reference disables a used one.
    key: 'tax-config',
    title: 'Tax Config',
    code: 'PC1203',
    addLabel: 'Add',
    itemLabel: 'Tax config line',
    fields: [
      {
        key: 'taxCompany',
        label: 'Tax Company',
        type: 'select',
        options: ['CERETAX'],
        required: true,
      },
      {
        key: 'transactionType',
        label: 'Transaction Type',
        type: 'select',
        options: TAX_TRANSACTION_TYPES,
        required: true,
      },
      { key: 'psCode', label: 'PS Code', type: 'digits', length: 8, required: true },
      {
        key: 'customerType',
        label: 'Customer Type',
        type: 'select',
        options: TAX_CUSTOMER_TYPES,
        required: true,
      },
      {
        key: 'taxIncludedCode',
        label: 'Tax Included Code',
        type: 'select',
        options: ['No', 'Yes'],
        required: true,
      },
      { key: 'taxType', label: 'Tax Type', type: 'text', maxLength: 200 },
      { key: 'taxTypeClass', label: 'Tax Type Class', type: 'text', maxLength: 200 },
      { key: 'taxLevel', label: 'Tax Level', type: 'multiselect', options: TAX_LEVELS },
      {
        key: 'businessType',
        label: 'Business Type',
        type: 'select',
        options: TAX_BUSINESS_TYPES,
        required: true,
      },
      {
        key: 'taxSitusRule',
        label: 'Tax Situs Rule',
        type: 'select',
        options: TAX_SITUS_RULES,
        required: true,
      },
    ],
    unique: ['taxCompany', 'transactionType'],
    status: true,
    searchLabel: 'Transaction Type or PS Code',
    searchKeys: ['transactionType', 'psCode'],
    note: 'Under which product code (PS Code) each kind of charge goes to the tax engine. No tax engine is connected, so these lines calculate nothing: tax comes from the Tax Rates tab when "Calculate tax" is on in System Config. Tax Type and Tax Type Class (exemptions) take the engine\'s codes, comma separated, for example 101, 103.',
  },
  {
    key: 'tax-rates',
    title: 'Tax Rates',
    code: 'PC1203',
    addLabel: 'Add Tax Rate',
    itemLabel: 'Tax rate',
    fields: [
      { key: 'state', label: 'State', type: 'state', required: true },
      { key: 'ratePercent', label: 'Tax Rate (%)', type: 'percent', required: true },
    ],
    unique: ['state'],
    status: true,
    searchLabel: 'State',
    searchKeys: ['state'],
    note: 'This list is not in the reference. The reference sends each charge to a tax service (SureTax); no such service is connected, so with "Calculate tax" on in System Config a charge is taxed at the Active rate of the customer\'s state here, and at 0 for a state without one. One rate per state cannot stand for real telecom tax, which differs by city and by kind of service.',
  },
  {
    key: 'commission-matrix',
    title: 'Commission matrix',
    code: 'PC792',
    addLabel: 'Add matrix commission',
    itemLabel: 'Commission matrix',
    fields: [
      { key: 'name', label: 'Matrix name', type: 'text', required: true, maxLength: 100 },
      {
        key: 'commissionType',
        label: 'Commission type',
        type: 'select',
        options: ['Recurring', 'Onetime'],
        required: true,
      },
      { key: 'months', label: 'Months', type: 'number', min: 1, max: 11 },
      {
        key: 'recurringPlan',
        label: 'Recurring for plan commission',
        type: 'select',
        options: YES_NO,
      },
      {
        key: 'recurringDevice',
        label: 'Recurring for device commission',
        type: 'select',
        options: YES_NO,
      },
    ],
    unique: ['name'],
    status: true,
    searchLabel: 'Matrix name',
    searchKeys: ['name'],
    note: 'The header of a matrix. Its amounts (per state, plan and category, and the recurring months) are kept by the Commission matrix page (`/commission-matrices`); this list is the record users point at.',
  },
  {
    key: 'shipper-settings',
    title: 'Shipper Settings',
    code: 'PC236',
    addLabel: 'Add Shipper',
    itemLabel: 'Shipper',
    fields: [
      {
        key: 'shipper',
        label: 'Shipper',
        type: 'select',
        options: SETTING_SHIPPERS,
        required: true,
      },
      { key: 'isDefault', label: 'Is Default', type: 'select', options: YES_NO, required: true },
      { key: 'company', label: 'Company', type: 'text', required: true, maxLength: 80 },
      { key: 'firm', label: 'Firm/Company', type: 'text', required: true, maxLength: 80 },
      { key: 'name', label: 'Name', type: 'text', required: true, maxLength: 80 },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'phone', label: 'Phone', type: 'digits', length: 10, required: true },
      { key: 'line1', label: 'Address Line1', type: 'text', required: true, maxLength: 120 },
      { key: 'line2', label: 'Address Line2', type: 'text', maxLength: 120 },
      { key: 'city', label: 'City', type: 'text', required: true, maxLength: 60 },
      { key: 'state', label: 'State', type: 'state', required: true },
      { key: 'zip', label: 'ZIP', type: 'zip', required: true },
      { key: 'country', label: 'Country', type: 'text', required: true, maxLength: 60 },
      ...ENDICIA_PACKAGE_FIELDS,
    ],
    unique: ['shipper'],
    status: true,
    searchLabel: 'Shipper or Company',
    searchKeys: ['shipper', 'company', 'firm'],
    note: 'No shipper account is connected, so the account credentials of the reference form are deliberately not asked for or stored. The ship-from address is sent with every label request, a shipper set to Inactive cannot be used for a label (assumption), and one shipper at a time is the default. The Endicia package settings are kept and sent with each Endicia label request.',
  },
  {
    key: 'linked-topups',
    title: 'Linked Topup',
    code: 'PC232',
    addLabel: 'Link Topup',
    itemLabel: 'Link',
    fields: [
      {
        key: 'carrierCode',
        label: 'Carrier Code',
        type: 'ref',
        source: 'carrierCode',
        required: true,
      },
      {
        key: 'planType',
        label: 'Plan Type',
        type: 'select',
        options: ['Service Plan', 'Topup'],
        required: true,
      },
      {
        key: 'topups',
        label: 'Link Topup',
        type: 'ref',
        source: 'topup',
        multiple: true,
        required: true,
      },
      { key: 'ildCarrier', label: 'ILD Carrier', type: 'text', maxLength: 60 },
      {
        key: 'sources',
        label: 'Purchase Allowed Source',
        type: 'multiselect',
        options: SALES_CHANNELS,
      },
    ],
    unique: ['carrierCode', 'planType'],
    status: false,
    searchLabel: 'ILD Carrier',
    searchKeys: ['ildCarrier', 'planType'],
    derived: [{ key: 'carrier', header: 'Carrier', after: 'planType' }],
    note: 'The links are recorded as the reference list shows them. Its dialog was not seen, and nothing checks a link yet when a top-up is applied.',
  },
];

export const getSettingsList = (key: string): SettingsListDef | undefined =>
  SETTINGS_LISTS.find((d) => d.key === key);

/** A value of a record: text, a number (cents or a count), or a list of texts or ids. */
export type SettingsValue = string | number | string[] | null;

const OBJECT_ID = /^[a-f\d]{24}$/i;

function fieldSchema(f: SettingsField): z.ZodTypeAny {
  const required = `${f.label} is required`;
  const pick = `Select ${f.label}`;
  switch (f.type) {
    case 'text': {
      const base = z
        .string({ required_error: required })
        .trim()
        .max(f.maxLength ?? 200);
      return f.required ? base.min(1, required) : base.default('');
    }
    case 'email': {
      const base = z
        .string({ required_error: required })
        .trim()
        .min(1, required)
        .max(120)
        .email(`${f.label} is not valid`);
      return f.required ? base : base.or(z.literal('')).default('');
    }
    case 'digits':
    case 'zip': {
      const length = f.type === 'zip' ? 5 : f.length;
      const base = z
        .string({ required_error: required })
        .trim()
        .regex(new RegExp(`^\\d{${length}}$`), `${f.label} must be ${length} digits`);
      return f.required ? base : base.or(z.literal('')).default('');
    }
    case 'state': {
      const base = z
        .string({ required_error: pick })
        .refine((v) => (US_STATE_CODES as readonly string[]).includes(v), pick);
      return f.required ? base : base.or(z.literal('')).default('');
    }
    case 'select': {
      const base = z.string({ required_error: pick }).refine((v) => f.options.includes(v), pick);
      return f.required ? base : base.or(z.literal('')).default('');
    }
    case 'multiselect': {
      const base = z.array(z.string().refine((v) => f.options.includes(v), pick)).max(50);
      return f.required ? base.min(1, pick) : base.default([]);
    }
    case 'percent': {
      const base = z
        .number({ required_error: required, invalid_type_error: required })
        .int(`${f.label} has at most two decimals`)
        .min(0, `${f.label} is at least 0`)
        .max(100_00, `${f.label} is at most 100`);
      return f.required ? base : base.nullable().default(null);
    }
    case 'money':
    case 'number': {
      const [min, max] = f.type === 'money' ? [0, 99_999_999] : [f.min, f.max];
      const base = z
        .number({ required_error: required, invalid_type_error: required })
        .int(`${f.label} must be a whole number`)
        .min(min, `${f.label} is at least ${f.type === 'money' ? '0' : min}`)
        .max(max, `${f.label} is too large`);
      return f.required ? base : base.nullable().default(null);
    }
    case 'ref': {
      const id = z.string({ required_error: pick }).regex(OBJECT_ID, pick);
      if (f.multiple) {
        const many = z.array(id).max(100);
        return f.required ? many.min(1, pick) : many.default([]);
      }
      return f.required ? id : id.or(z.literal('')).default('');
    }
  }
}

/** The schema of one record of a list: its fields and, where the list has one, a status. */
/** The value of a field that is not filled in. */
export const emptySettingsValue = (f: SettingsField): SettingsValue =>
  f.type === 'multiselect' || (f.type === 'ref' && f.multiple)
    ? []
    : f.type === 'money' || f.type === 'percent' || f.type === 'number'
      ? null
      : '';

/** Whether a field applies to a record with these values (its `when` holds). */
export const settingsFieldApplies = (f: SettingsField, values: Record<string, unknown>): boolean =>
  !f.when || values[f.when.key] === f.when.equals;

export function settingsRecordSchema(def: SettingsListDef) {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const f of def.fields) shape[f.key] = fieldSchema(f);
  if (def.status) shape.status = recordStatus.default('Active');
  // A field that does not apply (another shipper's block) is stored empty.
  return z.object(shape).transform((values) => {
    for (const f of def.fields)
      if (!settingsFieldApplies(f, values)) values[f.key] = emptySettingsValue(f);
    return values;
  });
}

export const settingsListQuerySchema = listQuerySchema.extend({
  q: optionalText(100),
  status: recordStatus.optional(),
});

export interface SettingsRecordDto {
  id: string;
  /** Running number within the list. */
  number: number;
  values: Record<string, SettingsValue>;
  /** Display text of reference fields and of derived columns, by key. */
  labels: Record<string, string>;
  status: RecordStatus;
  createdAt: string;
  createdBy: UserRef | null;
  updatedAt: string;
  updatedBy: UserRef | null;
}

/** Choices of the reference fields of a list, by field key. */
export type SettingsRefOptions = Record<string, Array<{ id: string; label: string }>>;

/* ------------------------- Other Settings landing page ---------------------- */

/**
 * "Note Type (Report Note Type)" (PC451), heading "Report Note Type Generic": a screen of
 * the reports engine. Filters and columns are the reference's.
 */
export const REPORT_NOTE_TYPE_DEF: ReportDef = {
  key: 'report-note-type',
  group: 'screen',
  name: 'Report Note Type Generic',
  heading: 'Report Note Type Generic',
  code: 'PC451',
  filters: [
    { type: 'dateRange', key: 'date', label: 'Date' },
    { type: 'select', key: 'status', label: 'Status', options: ['Open', 'Close'], required: true },
  ],
  columns: [
    reportColumn('customerId', 'Customer ID'),
    reportColumn('notes', 'Notes'),
    reportColumn('createdAt', 'Create Date', 'datetime'),
    reportColumn('postedBy', 'Posted By'),
    reportColumn('ticketNo', 'Ticket #'),
    reportColumn('status', 'Status'),
  ],
};
registerReportDefs([REPORT_NOTE_TYPE_DEF]);

/** The twelve links of the Other Settings landing page (PC240), in the reference's order. */
export const OTHER_SETTINGS_LINKS: ReadonlyArray<{ title: string; code: string; path: string }> = [
  {
    title: 'Manage Discount Credit Types',
    code: 'PC843',
    path: '/settings/other/list/discount-credit-types',
  },
  { title: 'Email Blacklist', code: 'PC984', path: '/settings/other/list/email-blacklist' },
  { title: 'Phone Number Blacklist', code: 'PC984', path: '/settings/other/list/phone-blacklist' },
  { title: 'Address Blacklist', code: 'PC984', path: '/settings/other/list/address-blacklist' },
  { title: 'Disconnection Reason', code: 'PC448', path: '/settings/other/disconnectReason' },
  { title: 'Note Type (Bulk Upload Notes)', code: 'PC449', path: '/settings/other/bulk-notes' },
  { title: 'Manage Note Type', code: 'PC450', path: '/settings/other/noteType' },
  { title: 'Manage Sub Note Type', code: 'PC1044', path: '/settings/other/list/sub-note-types' },
  {
    title: 'Note Type (Report Note Type)',
    code: 'PC451',
    path: '/settings/other/report-note-type',
  },
  {
    title: 'Payment Processing Fee Config',
    code: 'PC721',
    path: '/settings/other/processing-fees',
  },
  { title: 'Suspend Reason', code: 'PC996', path: '/settings/other/suspendReason' },
  { title: 'Manage Promo Discounts', code: 'PC1076', path: '/settings/other/list/promo-discounts' },
];
